/**
 * Alerts, Pipeline Health, Forecast, Benchmarks, and Deal Segmentation section.
 */
import React from 'react';
import { TrendingUp, DollarSign, CheckCircle, AlertCircle, AlertTriangle, Activity, Target, Bell } from 'lucide-react';
import { ChartWithDrillDown, commonColumns } from './DrillDownHelpers';

export default function PipelineForecast({ analytics }) {
  if (!analytics) return null;
  
  // Prepare drill-down data for pipeline health (pending proposals)
  const pendingProposalsData = analytics.detailed_scenarios?.filter(s => 
    ['pending_approval', 'pending_cfo_approval', 'pending_ceo_approval'].includes(s.status)
  ) || [];
  
  // Add days pending calculation
  const pendingWithDays = pendingProposalsData.map(p => {
    const submitted = p.submitted_at ? new Date(p.submitted_at) : new Date(p.created_at);
    const now = new Date();
    const daysPending = Math.floor((now - submitted) / (1000 * 60 * 60 * 24));
    return { ...p, days_pending: daysPending };
  });
  
  // At-risk proposals (>14 days pending)
  const atRiskData = pendingWithDays.filter(p => p.days_pending > 14);
  
  // Stalled proposals (>30 days pending)
  const stalledData = pendingWithDays.filter(p => p.days_pending > 30);
  
  return (
    <>
      {/* Alerts */}
      {analytics.alerts?.length > 0 && (
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-slate-900">Alerts & Notifications</h3>
            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
              {analytics.alerts.filter(a => a.priority === 'high').length} High Priority
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analytics.alerts.map((alert, idx) => {
              const typeMap = {
                error: { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-900', btn: 'text-red-700', Icon: AlertTriangle, iconColor: 'text-red-600' },
                warning: { border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-900', btn: 'text-yellow-700', Icon: AlertCircle, iconColor: 'text-yellow-600' },
                success: { border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-900', btn: 'text-green-700', Icon: CheckCircle, iconColor: 'text-green-600' },
                info: { border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-900', btn: 'text-blue-700', Icon: Activity, iconColor: 'text-blue-600' },
              };
              const t = typeMap[alert.type] || typeMap.info;
              return (
                <div key={idx} className={`p-4 rounded-lg border-l-4 ${t.border} ${t.bg}`}>
                  <div className="flex items-start gap-3">
                    <t.Icon className={`w-5 h-5 flex-shrink-0 ${t.iconColor}`} />
                    <div className="flex-1">
                      <h4 className={`text-sm font-semibold ${t.text}`}>{alert.title}</h4>
                      <p className="text-xs text-slate-600 mt-1">{alert.message}</p>
                      <button className={`mt-2 text-xs font-medium ${t.btn}`}>{alert.action} →</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pipeline Health & Forecast */}
      {analytics.pipeline_health && analytics.forecast && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartWithDrillDown
            title="Pipeline Health"
            subtitle={`Health Score: ${analytics.pipeline_health.health_score}/100`}
            data={pendingWithDays}
            columns={commonColumns.pipelineHealth}
            className="lg:col-span-1"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-600" />
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                analytics.pipeline_health.status === 'excellent' ? 'bg-green-100 text-green-700' :
                analytics.pipeline_health.status === 'good' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>{analytics.pipeline_health.health_score}/100</div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Pending Proposals</span>
                <span className="text-lg font-bold text-slate-900">{analytics.pipeline_health.pending_count}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Avg Days Pending</span>
                <span className="text-lg font-bold text-slate-900">{analytics.pipeline_health.avg_days_pending}d</span>
              </div>
              {analytics.pipeline_health.at_risk_count > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <span className="text-sm text-yellow-700">At Risk (&gt;14 days)</span>
                  <span className="text-lg font-bold text-yellow-900">{analytics.pipeline_health.at_risk_count}</span>
                </div>
              )}
              {analytics.pipeline_health.stalled_count > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-sm text-red-700">Stalled (&gt;30 days)</span>
                  <span className="text-lg font-bold text-red-900">{analytics.pipeline_health.stalled_count}</span>
                </div>
              )}
            </div>
          </ChartWithDrillDown>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-slate-900">Revenue Forecast</h3>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                analytics.forecast.confidence === 'high' ? 'bg-green-100 text-green-700' :
                analytics.forecast.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-slate-100 text-slate-700'
              }`}>{analytics.forecast.confidence} confidence</div>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-sm text-green-700 mb-1">Weighted Pipeline</p>
                <p className="text-3xl font-bold text-green-900">${(analytics.forecast.weighted_pipeline / 1000).toFixed(1)}K</p>
                <p className="text-xs text-green-600 mt-1">Probability-adjusted value</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600 mb-1">Monthly</p>
                  <p className="text-lg font-bold text-slate-900">${(analytics.forecast.monthly_forecast / 1000).toFixed(1)}K</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600 mb-1">Quarterly</p>
                  <p className="text-lg font-bold text-slate-900">${(analytics.forecast.quarterly_forecast / 1000).toFixed(1)}K</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Pending Value:</span>
                <span className="font-semibold text-slate-900">${(analytics.forecast.pending_value / 1000).toFixed(1)}K</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Approved Value:</span>
                <span className="font-semibold text-green-600">${(analytics.forecast.approved_value / 1000).toFixed(1)}K</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Benchmarks & Deal Segmentation */}
      {analytics.benchmarks && analytics.deal_segmentation && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-slate-900">Performance vs Target</h3>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                analytics.benchmarks.performance_tier === 'top_performer' ? 'bg-green-100 text-green-700' :
                analytics.benchmarks.performance_tier === 'above_average' ? 'bg-blue-100 text-blue-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>{analytics.benchmarks.performance_tier.replace('_', ' ')}</div>
            </div>
            <div className="space-y-4">
              {Object.entries(analytics.benchmarks).filter(([key]) => key !== 'performance_tier').map(([metric, data]) => (
                <div key={metric} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 capitalize">{metric.replace('_', ' ')}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900">
                        {typeof data.current === 'number' ? data.current.toFixed(1) : data.current}
                        {metric.includes('rate') ? '%' : metric.includes('days') ? 'd' : ''}
                      </span>
                      {data.current >= data.target ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-yellow-600" />}
                    </div>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all ${data.current >= data.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                        style={{ width: `${Math.min((data.current / data.target) * 100, 100)}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                      <span>Target: {data.target}</span><span>Avg: {data.industry_avg}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-slate-900">Deal Size Segmentation</h3>
            </div>
            <div className="space-y-3">
              {Object.entries(analytics.deal_segmentation).map(([segment, data]) => (
                <div key={segment} className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-700 capitalize">{segment}</span>
                    <span className="text-sm text-slate-600">{data.count} deals</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div><p className="text-xs text-slate-500">Total Value</p><p className="text-sm font-bold text-green-600">${(data.total_value / 1000).toFixed(1)}K</p></div>
                    <div className="text-right"><p className="text-xs text-slate-500">Avg Deal</p><p className="text-sm font-bold text-slate-900">${(data.avg_value / 1000).toFixed(1)}K</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
